import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert, Box, Card, CardContent, Chip, CircularProgress, Divider,
  LinearProgress, Stack, Table, TableBody, TableCell, TableHead, TableRow,
  Tooltip, Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import api from '../../services/api';

/**
 * How fresh and how complete the local SIS mirror is.
 *
 * The dashboard reads a copy of SIS held in the toolbox database, not SIS
 * itself. When that copy is missing or half-built, panels either fail or
 * quietly fall back to near-empty local tables -- which is how "Active
 * Teachers: 2" once reached a screen under a "Real-time SIS Data" badge.
 *
 * This shows an administrator the mirror's own state, so a wrong figure can
 * be recognised as stale data rather than puzzled over.
 */

interface MirrorTable { name: string; label: string; exists: boolean; rows: number; }
interface MirrorView { name: string; exists: boolean; }
interface MirrorStatus {
  ready: boolean;
  last_sync: string | null;
  age_hours: number | null;
  stale: boolean;
  tables: MirrorTable[];
  views: MirrorView[];
  command: string;
}

const freshness = (s: MirrorStatus) => {
  if (s.age_hours === null) return { label: 'Never synced', color: 'error' as const };
  if (s.age_hours < 24) return { label: `Synced ${s.age_hours}h ago`, color: 'success' as const };
  const days = Math.round(s.age_hours / 24);
  return { label: `Last synced ${days} day${days === 1 ? '' : 's'} ago`, color: 'warning' as const };
};

const SisMirrorStatus: React.FC = () => {
  const { data, isLoading, error } = useQuery<MirrorStatus>({
    queryKey: ['sisMirrorStatus'],
    queryFn: async () => (await api.get('/sis-integration/mirror-status/')).data,
    // While a sync is running the counts climb; refresh often enough to see it.
    refetchInterval: 15000,
  });

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }
  if (error || !data) {
    return <Alert severity="error">Could not read the SIS mirror status.</Alert>;
  }

  const fresh = freshness(data);
  const missing = [
    ...data.tables.filter((t) => !t.exists || t.rows === 0).map((t) => t.label),
    ...data.views.filter((v) => !v.exists).map((v) => v.name),
  ];

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography variant="h6">SIS data copy</Typography>
          <Chip size="small" label={fresh.label} color={fresh.color} />
          {data.ready
            ? <Chip size="small" icon={<CheckCircleIcon />} label="Complete" color="success" variant="outlined" />
            : <Chip size="small" icon={<ErrorOutlineIcon />} label="Incomplete" color="warning" variant="outlined" />}
        </Stack>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Dashboards read this copy of SIS, not SIS directly. Anything missing
          below is a panel that will be wrong or empty on screen.
        </Typography>

        {!data.ready && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>Not everything has been copied.</strong>{' '}
            {missing.length > 0 && <>Missing: {missing.join(', ')}. </>}
            Run <code>{data.command}</code> on the server.
          </Alert>
        )}
        {data.ready && data.stale && (
          <Alert severity="info" sx={{ mb: 2 }}>
            The copy is complete but over a day old. Figures shown are from the
            last sync, not from SIS as it stands now.
          </Alert>
        )}

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Data</TableCell>
              <TableCell align="right">Records copied</TableCell>
              <TableCell align="right">State</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.tables.map((t) => {
              const ok = t.exists && t.rows > 0;
              return (
                <TableRow key={t.name}>
                  <TableCell>
                    <Tooltip title={`sis.${t.name}`}><span>{t.label}</span></Tooltip>
                  </TableCell>
                  <TableCell align="right">{ok ? t.rows.toLocaleString() : '—'}</TableCell>
                  <TableCell align="right">
                    <Chip size="small" label={ok ? 'Copied' : 'Not copied'}
                          color={ok ? 'success' : 'default'}
                          variant={ok ? 'filled' : 'outlined'} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>Summary tables</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Built after the copy. Without these the dashboard falls back to old
          local figures even when everything above is copied.
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {data.views.map((v) => (
            <Chip key={v.name} size="small" label={v.name.replace(/^v_/, '')}
                  color={v.exists ? 'success' : 'warning'}
                  variant={v.exists ? 'filled' : 'outlined'} />
          ))}
        </Stack>

        {!data.ready && <LinearProgress sx={{ mt: 2 }} />}
      </CardContent>
    </Card>
  );
};

export default SisMirrorStatus;
